import { Controller } from 'stimulus'
import { DonutGraph } from 'classes/donut-graph'
import { AmortizationGraph } from 'classes/amortization-graph'
import { ScrollyAmortizationTable } from 'classes/scrolly-amortization-table'

import * as MortMath from 'modules/mort-math'
import * as MortDataPrep from 'modules/mort-data-prep'
import moment from 'moment'

import roundNumber from 'helpers/round-number'
import parseFloatString from 'helpers/parse-float-string'
import formatCurrency from 'helpers/new-format-currency'

/**
 * Manages js for the mortgage payment page (StimulusJS controller)
 *
 * @class MortgagePaymentController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'homePrice',
    'loanTerm',
    'interestRate',
    'downPayment',
    'downPaymentPercent',
    'propertyTax',
    'propertyTaxPercent',
    'homeInsurance',
    'hoaDues',
    'includesPMI',
    'amortizationGraph',
    'amortizationTable',
    'donutGraph',
    'overallTerm',
    'totalInterest',
    'payoffDate',
    'toggleAmortizationTable'
  ]

  timeout = null

  /**
   * @function Controllers.MortgagePaymentController.initialize
   * @memberof Controllers
   * @description Called on page load. Calculates the loan schedule and displays dynamic entities
   */
  initialize() {
    this.termToggle = 'monthly'

    const { loanSchedule, textualData, donutData, monthlyPayment, nextMonth } = this._calculateData()

    this.donutGraph = new DonutGraph(this.donutGraphTarget, Object.keys(donutData))
    this.donutGraph.change(donutData)

    if (this.amortizationGraphTarget) {
      const { graphData }    = this._prepareGraphData(loanSchedule, nextMonth)
      this.amortizationGraph = new AmortizationGraph(this.amortizationGraphTarget, 'Yrs')
      this.amortizationGraph.build(graphData)
    }

    if (this.amortizationTableTarget) {
      const amortizationTableData   = MortDataPrep.monthlyTableData(loanSchedule, monthlyPayment, 'YYYY/M')
      this.scrollyAmortizationTable = new ScrollyAmortizationTable(this.amortizationTableTarget)
      this.scrollyAmortizationTable.build(amortizationTableData)

      this._setTextualData(textualData)
    }
  }

  /**
   * @function Controllers.MortgagePaymentController.process
   * @memberof Controllers
   * @description Called on `keyup` event and `toggle()`. Sets dynamic form values and then calculates the loan schedule anew and displays dynamic entities.
   *
   * @param {Event} event
   */
  process(event) {
    clearTimeout(this.timeout)

    this.amortizationTableTarget.dispatchEvent(new CustomEvent('table:collapse'))

    this.timeout = setTimeout(() => {
      this._setDynamicFields(event)

      const { loanSchedule, textualData, donutData, monthlyPayment, nextMonth } = this._calculateData()

      this.donutGraph.change(donutData)

      const { graphData, yearlySchedule } = this._prepareGraphData(loanSchedule, nextMonth)

      if (this.amortizationGraph) {
        this.amortizationGraph.build(graphData)
      }

      let amortizationTableData

      if (this.termToggle === 'annually') {
        amortizationTableData = MortDataPrep.yearlyTableData(yearlySchedule, monthlyPayment, 'YYYY')
      } else {
        amortizationTableData = MortDataPrep.monthlyTableData(loanSchedule, monthlyPayment, 'YYYY/M')
      }

      this.scrollyAmortizationTable.build(amortizationTableData)

      this._setTextualData(textualData)
    }, 400)
  }

  /**
   * @function Controllers.MortgagePaymentController.toggle
   * @memberof Controllers
   * @description Toggles graph between monthly and yearly. Called on `click` event
   *
   * @param {Event} event - Event from user interaction
   *
   * @return {undefined} manipulates DOM
   */
  toggle(event) {
    const toggleButtons = document.querySelectorAll(`[data-target="${this.toggleAmortizationTableTarget.dataset.target}"]`)

    Array.from(toggleButtons).map(button => {
      button.classList.remove('mortgage-calculator__amortization-information-toggle-button--active')
    })

    event.target.classList.add('mortgage-calculator__amortization-information-toggle-button--active')

    this.termToggle = event.target.dataset.value

    this.process()
  }

  // private

  /**
   * @function Controllers.MortgagePaymentController._prepareFormValues
   * @memberof Controllers
   * @description Takes inputs from the form and prepares them for MortMath consumption and MortDataPrep manipulation.
   * @private
   *
   * @return {Object} - data, all the form values plus some light calculated values (i.e. monthlyTaxes)
   *
   * @example
   *
   * return = {
   *   interestPercent,
   *   loanTermInYears,
   *   downPayment,
   *   homePrice,
   *   yearlyTaxes,
   *   taxPercent,
   *   downPayment,
   *   downPaymentPercent,
   *   HOADues,
   *   yearlyInsurance,
   *   monthlyTaxes,
   *   monthlyInsurance,
   *   loanAmount,
   *   loanTermInMonths,
   * }
   */
  _prepareFormValues() {
    const interestPercent    = parseFloatString(this.interestRateTarget.value)
    const loanTermInYears    = parseFloatString(this.loanTermTarget.value)
    const downPayment        = parseFloatString(this.downPaymentTarget.value)
    const homePrice          = parseFloatString(this.homePriceTarget.value)
    const yearlyTaxes        = parseFloatString(this.propertyTaxTarget.value)
    const taxPercent         = parseFloatString(this.propertyTaxPercentTarget.value)
    const downPaymentPercent = parseFloatString(this.downPaymentPercentTarget.value)
    const HOADues            = parseFloatString(this.hoaDuesTarget.value)
    const yearlyInsurance    = parseFloatString(this.homeInsuranceTarget.value)

    const monthlyTaxes     = yearlyTaxes / 12
    const monthlyInsurance = yearlyInsurance / 12
    const loanAmount       = homePrice - downPayment
    const loanTermInMonths = loanTermInYears * 12

    const data = {
      interestPercent,
      loanTermInYears,
      downPayment,
      homePrice,
      yearlyTaxes,
      taxPercent,
      downPaymentPercent,
      HOADues,
      yearlyInsurance,
      monthlyTaxes,
      monthlyInsurance,
      loanAmount,
      loanTermInMonths
    }

    return { data }
  }


  /**
   * @function Controllers.MortgagePaymentController._calculateData
   * @memberof Controllers
   * @description A phoenix rises from the ashes of calculations past... Takes inputs from the UI and gets calculations from MortMath
   * @private
   *
   * @return {Object}
   *
   * @example
   *
   * return = {
   *   loanSchedule, - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
   *   textualData: {
   *     loanEnd:         'July 2038',
   *     loanTermInYears: 30,
   *     totalInterest:   221200,
   *   }
   *   donutData: {
   *     'HOA':                  85,
   *     'Insurance':            1200,
   *     'PMI':                  78,
   *     'Principal & Interest': 1442,
   *     'Taxes':                126,
   *   }
   *   monthlyPayment,
   *   nextMonth,
   * }
   *
   */
  _calculateData() {
    const nextMonth = moment().add(1, 'M')
    const { data } = this._prepareFormValues()

    const monthlyInterestPercent = MortMath.monthlyInterestPercent(data.interestPercent)
    const monthlyPayment         = MortMath.monthlyPayment({
      loanTermInMonths:       data.loanTermInMonths,
      monthlyInterestPercent: monthlyInterestPercent,
      principal:              data.loanAmount
    })

    const loanDetails = {
      balance:                data.loanAmount || 0,
      date:                   nextMonth,
      monthlyInterestPercent: monthlyInterestPercent || 0,
      monthlyPayment:         monthlyPayment || 0
    }

    const loanSchedule  = MortMath.amortizationSchedule(loanDetails)
    const loanEnd       = MortDataPrep.loanEnd(loanSchedule)
    const totalInterest = MortMath.totalInterestFrom(loanSchedule, loanDetails.date)

    let pmi = 0

    if (this.includesPMITarget.checked) {
      pmi = MortMath.calculatePMI(data.loanAmount, data.downPayment, data.downPaymentPercent)
    }

    const textualData = {
      loanEnd,
      loanTermInYears: data.loanTermInYears,
      totalInterest
    }

    const donutData = {
      'HOA':                  data.HOADues,
      'Insurance':            data.monthlyInsurance,
      'PMI':                  pmi,
      'Principal & Interest': monthlyPayment,
      'Taxes':                data.monthlyTaxes
    }

    return {
      loanSchedule,
      textualData,
      donutData,
      monthlyPayment,
      nextMonth
    }
  }


  /**
   * @function Controllers.MortgagePaymentController._prepareGraphData
   * @memberof Controllers
   * @description Returns formatted data from a schedule suitable for d3 graph
   * @private
   *
   * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
   *
   * @return {Object} data
   * @return {Array.<Object>} data.graphData - labels and data for a loan
   * @return {Object} data.yearlySchedule - the yearly schedule
   *
   * @example
   *
   * returns = {
   *   graphData: [
   *     {
   *       label: "Yrs",
   *       data: [ 0, 1, 2, ... ],
   *     },
   *     {
   *       label: "Interest",
   *       data: [ 5098.822433980482, 17185.782102743888, 29053.507463941045, ... ],
   *     },
   *     {
   *       label: "Principal",
   *       data: [ 2110.342336927378, 7325.378118342836, 12759.648207324546, ... ]
   *      },
   *     {
   *       label: "Remaining",
   *       data: [ 295389.6576630726, 290174.62188165705, 284740.3517926753, ... ]
   *     },
   *   ],
   *   yearlySchedule: schedule, - reslut of [MortMath.yearlyAmortizationSchedule]{@link module:MortMath.yearlyAmortizationSchedule}
   */
  _prepareGraphData(schedule) {
    const yearlySchedule = MortMath.yearlyAmortizationSchedule(schedule)
    const yearlyData     = MortMath.yearlyOverallTrendingValues(yearlySchedule)

    const graphData = MortDataPrep.graphData(yearlyData)

    return { graphData, yearlySchedule }
  }


  /**
   * @function Controllers.MortgagePaymentController._setTextualData
   * @memberof Controllers
   * @description Displays the textual calculated results
   * @private
   *
   * @param {Object} textData
   * @param {Float} textData.totalInterest - result of [MortMath.totalInterestFrom]{@link module.MortMath.totalInterestFrom}
   * @param {String} textData.loanEnd - "July 2038"
   * @param {Integer} textData.loanTermInYears - 30
   *
   * @return {undefined} manipulates DOM
   */
  _setTextualData({ totalInterest, loanEnd, loanTermInYears }) {
    this.overallTermTarget.innerHTML   = loanTermInYears
    this.totalInterestTarget.innerHTML = formatCurrency(totalInterest)
    this.payoffDateTarget.innerHTML    = loanEnd
  }


  /**
   * @function Controllers.MortgagePaymentController._setDynamicFields
   * @memberof Controllers
   * @description Resets fields in the DOM for values that are tied, i.e. homePrice changing means
   * the downPayment and propertyTax fields must change accordingly. This action occurs prior to
   * the calculation event, in which these newly set values will be used.
   * @private
   *
   * @param {Event} event - The event which set off `process`, used to tell if a field of interest has changed.
   *
   * @return {undefined} sets DOM values in the appropriate fields
   */
  _setDynamicFields(event) {
    if (event === undefined) { return }

    const changedField = event.target.dataset.target.split('.')[1]
    const value        = event.target.value

    switch (changedField) {
      case 'homePrice':
        this._updateForHomePrice(value)
        break
      case 'downPayment':
        this._updateForDownPayment(value)
        break
      case 'downPaymentPercent':
        this._updateForDownPaymentPercent(value)
        break
      case 'propertyTax':
        this._updateForPropertyTax(value)
        break
      case 'propertyTaxPercent':
        this._updateForPropertyTaxPercent(value)
        break
    }
  }


  /**
   * @function Controllers.MortgagePaymentController._updateForPropertyTaxPercent
   * @memberof Controllers
   * @description As a result of a new propertyTaxPercent value, sets the propertyTax field
   * @private
   *
   * @param {Number} value - The new propertyTaxPercent value
   *
   * @return {undefined} sets DOM values in the appropriate fields
   */
  _updateForPropertyTaxPercent(value) {
    const propertyTaxPercent = parseFloatString(value) / 100

    const homePrice   = parseFloatString(this.homePriceTarget.value)
    const downPayment = parseFloatString(this.downPaymentTarget.value)

    const loanAmount  = homePrice - downPayment
    const propertyTax = loanAmount * propertyTaxPercent

    this.propertyTaxTarget.value = formatCurrency(propertyTax, '')
  }


  /**
   * @function Controllers.MortgagePaymentController._updateForPropertyTax
   * @memberof Controllers
   * @description As a result of a new propertyTax value, sets the propertyTaxPercent field
   * @private
   *
   * @param {Number} value - The new propertyTax value
   *
   * @return {undefined} sets DOM values in the appropriate fields
   */
  _updateForPropertyTax(value) {
    const propertyTax = parseFloatString(value)

    const homePrice   = parseFloatString(this.homePriceTarget.value)
    const downPayment = parseFloatString(this.downPaymentTarget.value)

    const loanAmount         = homePrice - downPayment
    const propertyTaxPercent = (propertyTax / loanAmount) * 100

    this.propertyTaxPercentTarget.value = roundNumber(propertyTaxPercent)
  }


  /**
   * @function Controllers.MortgagePaymentController._updateForDownPaymentPercent
   * @memberof Controllers
   * @description As a result of a new downPaymentPercent value, sets the downPayment and propertyTax fields.
   * @private
   *
   * @param {Number} value - The new downPaymentPercent value
   *
   * @return {undefined} sets DOM values in the appropriate fields
   */
  _updateForDownPaymentPercent(value) {
    const downPaymentPercent = parseFloatString(value) / 100

    const homePrice          = parseFloatString(this.homePriceTarget.value)
    const propertyTaxPercent = parseFloatString(this.propertyTaxPercentTarget.value) / 100

    const downPayment = homePrice * downPaymentPercent
    const loanAmount  = homePrice - downPayment
    const propertyTax = loanAmount * propertyTaxPercent

    this.downPaymentTarget.value = formatCurrency(downPayment, '')
    this.propertyTaxTarget.value = formatCurrency(propertyTax, '')
  }


  /**
   * @function Controllers.MortgagePaymentController._updateForDownPayment
   * @memberof Controllers
   * @description As a result of a new downPayment value, sets the downPaymentPercent and propertyTax fields.
   * @private
   *
   * @param {Number} value - The new downPayment value
   *
   * @return {undefined} sets DOM values in the appropriate fields
   */
  _updateForDownPayment(value) {
    const downPayment        = parseFloatString(value)

    const homePrice          = parseFloatString(this.homePriceTarget.value)
    const propertyTaxPercent = parseFloatString(this.propertyTaxPercentTarget.value) / 100

    const loanAmount         = homePrice - downPayment
    const downPaymentPercent = (downPayment / homePrice) * 100
    const propertyTax        = loanAmount * propertyTaxPercent

    this.downPaymentPercentTarget.value = roundNumber(downPaymentPercent)
    this.propertyTaxTarget.value        = formatCurrency(propertyTax, '')
  }


  /**
   * @function Controllers.MortgagePaymentController._updateForHomePrice
   * @memberof Controllers
   * @description As a result of a new homePrice value, sets the downPayment and propertyTax fields.
   * @private
   *
   * @param {Number} value - The new homePrice value
   *
   * @return {undefined} sets DOM values in the appropriate fields
   */
  _updateForHomePrice(value) {
    const homePrice          = parseFloatString(value)

    const downPaymentPercent = parseFloatString(this.downPaymentPercentTarget.value) / 100
    const propertyTaxPercent = parseFloatString(this.propertyTaxPercentTarget.value) / 100

    const downPayment = homePrice * downPaymentPercent
    const loanAmount  = homePrice - downPayment

    this.downPaymentTarget.value = formatCurrency(downPayment, '')
    this.propertyTaxTarget.value = formatCurrency(loanAmount * propertyTaxPercent, '')
  }
}
